import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@shared/lib';
import { fmtCalendarDay } from '../model/format';

/** Серии графиков — валидированная тёмная палитра (dataviz): синий, оранжевый, бирюзовый,
 *  проверено на поверхности #1e1e20 (CVD ΔE 9,4, контраст ≥ 3:1). Порядок фиксирован. */
export const SERIES_COLORS = ['#3987e5', '#d95926', '#199e70'] as const;

const GRID = '#2c2c2a';
const AXIS_TEXT = '#898781';
const SURFACE = '#1e1e20';

export interface ChartSeries {
  key: string;
  label: string;
  color: string;
  values: (number | null)[];
}

interface TimeChartProps {
  /** Дни YYYY-MM-DD по оси X. */
  days: string[];
  series: ChartSeries[];
  kind: 'bar' | 'stacked' | 'line';
  format: (value: number) => string;
  lang: string;
  height?: number;
  ariaLabel: string;
  /** Счётчики: шаг оси — целый, не меньше 1. */
  integer?: boolean;
  /** Линии: первая серия рисуется ещё и заливкой 10%, чтобы не пропасть под совпадающими. */
  area?: boolean;
}

const PAD = { top: 10, right: 8, bottom: 24 };

function niceMax(max: number, integer: boolean): { top: number; step: number } {
  if (max <= 0) return { top: integer ? 4 : 1, step: integer ? 1 : 0.25 };
  const raw = max / 4;
  const pow = 10 ** Math.floor(Math.log10(raw));
  let step = [1, 2, 2.5, 5, 10].map((m) => m * pow).find((s) => s >= raw) ?? 10 * pow;
  if (integer) step = Math.max(1, Math.ceil(step));
  return { top: Math.ceil(max / step) * step, step };
}


/** Столбец со скруглённым концом 4px и прямым основанием. */
function barPath(x: number, y: number, w: number, h: number, round: boolean): string {
  if (h <= 0) return '';
  const r = round ? Math.min(4, w / 2, h) : 0;
  return `M${x},${y + h}V${y + r}Q${x},${y} ${x + r},${y}H${x + w - r}Q${x + w},${y} ${x + w},${y + r}V${y + h}Z`;
}

export function TimeChart({
  days,
  series,
  kind,
  format,
  lang,
  height = 168,
  ariaLabel,
  integer = true,
  area = false,
}: TimeChartProps) {
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(560);
  const [hover, setHover] = useState<number | null>(null);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.max(240, entry.contentRect.width)));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const n = days.length;
  const { top, step } = useMemo(() => {
    let max = 0;
    for (let i = 0; i < n; i++) {
      const values = series.map((s) => s.values[i] ?? 0);
      max = Math.max(max, kind === 'stacked' ? values.reduce((a, b) => a + b, 0) : Math.max(0, ...values));
    }
    return niceMax(max, integer);
  }, [series, n, kind, integer]);
  const ticks = Array.from({ length: Math.round(top / step) + 1 }, (_, i) => i * step);
  // Отступ под подписи оси: по самой длинной (≈6,2 px на символ при 10,5 px).
  const left = Math.max(28, Math.max(...ticks.map((tick) => format(tick).length)) * 6.2 + 12);
  const plotW = width - left - PAD.right;
  const plotH = height - PAD.top - PAD.bottom;
  const band = plotW / Math.max(1, n);

  const y = (v: number) => PAD.top + plotH - (v / top) * plotH;
  const xCenter = (i: number) => left + band * i + band / 2;
  const barW = Math.max(2, Math.min(24, band * (kind === 'bar' && series.length > 1 ? 0.4 : 0.66)));
  const labelEvery = Math.max(1, Math.ceil(n / Math.max(2, Math.floor(plotW / 64))));

  const onMove = (event: React.PointerEvent<SVGSVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * width - left;
    const index = Math.floor(x / band);
    setHover(index >= 0 && index < n ? index : null);
  };

  const tipLeft = hover === null ? 0 : Math.min(Math.max(xCenter(hover) - 80, 0), width - 168);

  return (
    <div ref={box} className="relative w-full select-none">
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={ariaLabel}
        onPointerMove={onMove}
        onPointerLeave={() => setHover(null)}
        className="block touch-pan-y"
      >
        {ticks.map((tick) => (
          <g key={tick}>
            <line x1={left} x2={width - PAD.right} y1={y(tick)} y2={y(tick)} stroke={GRID} strokeWidth={1} />
            <text x={left - 8} y={y(tick) + 3.5} textAnchor="end" fontSize={10.5} fill={AXIS_TEXT} className="tabular-nums">
              {format(tick)}
            </text>
          </g>
        ))}
        {days.map((day, i) =>
          i % labelEvery === 0 || i === n - 1 ? (
            i === n - 1 || n - 1 - i >= labelEvery / 2 ? (
              <text key={day} x={xCenter(i)} y={height - 6} textAnchor="middle" fontSize={10.5} fill={AXIS_TEXT}>
                {fmtCalendarDay(day, lang)}
              </text>
            ) : null
          ) : null,
        )}
        {hover !== null && (
          <rect x={left + band * hover} y={PAD.top} width={band} height={plotH} fill="#ffffff" fillOpacity={0.04} />
        )}

        {kind === 'line'
          ? series.map((s, index) => {
              let d = '';
              let fill = '';
              let open = false;
              let startX = 0;
              let lastX = 0;
              s.values.forEach((v, i) => {
                if (v === null) {
                  if (open) fill += `L${lastX},${y(0)}L${startX},${y(0)}Z`;
                  open = false;
                  return;
                }
                if (!open) startX = xCenter(i);
                d += `${open ? 'L' : 'M'}${xCenter(i)},${y(v)}`;
                fill += `${open ? 'L' : 'M'}${xCenter(i)},${y(v)}`;
                lastX = xCenter(i);
                open = true;
              });
              if (open) fill += `L${lastX},${y(0)}L${startX},${y(0)}Z`;
              return (
                <g key={s.key}>
                  {area && index === 0 && <path d={fill} fill={s.color} fillOpacity={0.1} />}
                  <path d={d} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                </g>
              );
            })
          : days.map((day, i) => {
              if (kind === 'stacked') {
                let base = 0;
                const filled = series.filter((s) => (s.values[i] ?? 0) > 0);
                return (
                  <g key={day}>
                    {filled.map((s, k) => {
                      const v = s.values[i] ?? 0;
                      const y1 = y(base + v);
                      // 2px зазор цвета поверхности между сегментами.
                      const h = y(base) - y1 - (k > 0 ? 2 : 0);
                      base += v;
                      return <path key={s.key} d={barPath(xCenter(i) - barW / 2, y1, barW, h, k === filled.length - 1)} fill={s.color} />;
                    })}
                  </g>
                );
              }
              const group = barW * series.length + 2 * (series.length - 1);
              return (
                <g key={day}>
                  {series.map((s, k) => {
                    const v = s.values[i];
                    if (!v) return null;
                    const x = xCenter(i) - group / 2 + k * (barW + 2);
                    return <path key={s.key} d={barPath(x, y(v), barW, y(0) - y(v), true)} fill={s.color} />;
                  })}
                </g>
              );
            })}

        {hover !== null && kind === 'line' && (
          <g>
            <line x1={xCenter(hover)} x2={xCenter(hover)} y1={PAD.top} y2={PAD.top + plotH} stroke={AXIS_TEXT} strokeWidth={1} />
            {series.map((s) =>
              s.values[hover] === null ? null : (
                <circle key={s.key} cx={xCenter(hover)} cy={y(s.values[hover] ?? 0)} r={4} fill={s.color} stroke={SURFACE} strokeWidth={2} />
              ),
            )}
          </g>
        )}
        <line x1={left} x2={width - PAD.right} y1={y(0)} y2={y(0)} stroke="#383835" strokeWidth={1} />
      </svg>

      {hover !== null && (
        <div
          className="pointer-events-none absolute top-0 z-10 w-[168px] rounded-xl bg-[#26262b] px-3 py-2.5 shadow-[0_0_0_1px_#34343a,0_16px_32px_-12px_rgba(0,0,0,0.8)]"
          style={{ left: tipLeft }}
        >
          <div className="mb-1.5 text-[12px] text-muted">{fmtCalendarDay(days[hover], lang)}</div>
          {series.map((s) => (
            <div key={s.key} className="flex items-center gap-2 text-[14px] leading-[1.6]">
              <span className="h-[2px] w-3 shrink-0 rounded-full" style={{ background: s.color }} />
              <span className="font-semibold tabular-nums text-fg">
                {s.values[hover] === null ? '—' : format(s.values[hover] ?? 0)}
              </span>
              <span className="truncate text-muted">{s.label}</span>
            </div>
          ))}
        </div>
      )}

      <table className="sr-only">
        <caption>{ariaLabel}</caption>
        <tbody>
          {days.map((day, i) => (
            <tr key={day}>
              <th scope="row">{day}</th>
              {series.map((s) => (
                <td key={s.key}>{s.values[i] === null ? '—' : format(s.values[i] ?? 0)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Легенда повторяет форму метки: квадрат для столбцов, короткая линия для линий. */
export function Legend({
  items,
  className,
  lines,
}: {
  items: { label: string; color: string; value?: string }[];
  className?: string;
  lines?: boolean;
}) {
  return (
    <div className={cn('flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-muted', className)}>
      {items.map((item) => (
        <span key={item.label} className="inline-flex items-center gap-1.5">
          <span
            className={cn('shrink-0', lines ? 'h-[2px] w-3 rounded-full' : 'h-2.5 w-2.5 rounded-[3px]')}
            style={{ background: item.color }}
          />
          {item.label}
          {item.value && <span className="font-medium tabular-nums text-fg">{item.value}</span>}
        </span>
      ))}
    </div>
  );
}
